import { ADMIN_ROUTES } from 'constants/routes';
import { ErrorMappingValueType } from 'typings';
import { useCallback, useEffect, useState } from 'react';
import { Form, FormProps, notification } from 'components';
import { AdminResetPasswordFieldType } from 'typings/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PasswordResetCheckMapping, PasswordResetFinishMapping } from 'constants/validations/auth';
import { useAdminCheckPasswordResetMutation, useAdminFinishPasswordResetMutation, useAuthenticateMutation } from 'api';

export const useAdminResetPassword = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [invalidToken, setInvalidToken] = useState(false);
  const [checkPasswordReset, { isLoading: checkIsLoading }] = useAdminCheckPasswordResetMutation();
  const [finishPasswordReset, { isLoading: finishIsLoading }] = useAdminFinishPasswordResetMutation();
  const [authenticate] = useAuthenticateMutation();

  const [notificationApi, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const onFinish: FormProps<AdminResetPasswordFieldType>['onFinish'] = useCallback(
    (values: AdminResetPasswordFieldType) => {
      const payload = values;
      payload.resetToken = searchParams.get('key');
      finishPasswordReset(payload)
        .then(({ data, error }: any) => {
          if (data?.response_code === 'OK') {
            openNotificationWithIcon({
              type: 'success',
              message: 'Password successfully changed!',
            });
            navigate(ADMIN_ROUTES.adminResetPasswordSuccess);
          } else {
            if (data) {
              Object.keys(data).forEach((_key: string) => {
                if (_key === 'status') {
                  openNotificationWithIcon(PasswordResetFinishMapping[data[_key]]);
                } else {
                  _key &&
                    openNotificationWithIcon({
                      type: 'error',
                      message: data[_key],
                    });
                }
              });
            } else if (error?.data) {
              openNotificationWithIcon({
                type: 'error',
                message: 'Something went wrong!',
              });
            }
          }
        })
        .catch((err) => {
          console.log(err, 'error');
        });
    },
    [finishPasswordReset, searchParams, navigate, openNotificationWithIcon]
  );

  useEffect(() => {
    if (searchParams.get('key')) {
      authenticate();
      checkPasswordReset({ resetToken: searchParams.get('key') })
        .then(({ data, error }: any) => {
          if (!data?.data?.email) {
            if (data.data) {
              Object.keys(data).forEach((_key: string) => {
                if (_key === 'status') {
                  openNotificationWithIcon(PasswordResetCheckMapping[data[_key]]);
                } else {
                  _key &&
                    openNotificationWithIcon({
                      type: 'error',
                      message: data[_key],
                    });
                }
              });
            } else if (error?.data) {
              openNotificationWithIcon({
                type: 'error',
                message: 'Something went wrong!',
              });
            }
          }
        })
        .catch(() => {
          setInvalidToken(true);
        });
    }
  }, [checkPasswordReset, authenticate, searchParams, openNotificationWithIcon]);

  return {
    form,
    isLoading: checkIsLoading || finishIsLoading,
    onFinish,
    contextHolder,
    invalidToken,
  };
};
